import * as React from 'react';
import {
  StyledCheckboxIcon,
  StyledHiddenCheckbox,
  StyledCheckbox,
  IStyledCheckboxProps
} from './Checkbox.style';
import { useCheckboxGroupContext } from '../context';

export interface ICheckboxProps extends IStyledCheckboxProps {
  /**
   * Value of this checkbox.
   */
  value?: string;

  /**
   * Whether the checkbox should be ticked or not ticked by default.
   *
   * @default false
   */
  checked?: boolean;

  /**
   * Name of checkbox to bind the same checkbox group;
   * Holds context passed from CheckboxGroup
   */
  name?: string;

  /**
   * Unique identifier for screen readers to associate this component
   * with the label that describes it
   */
  id: string;

  /**
   * Class to be applied to checkbox
   */
  className?: string;

  /**
   * Determines if the checkbox is checked by default (for uncontrolled states)
   */
  defaultChecked?: boolean;

  /**
   * Replaces svg for checked state to symbolized an intermediate, "partial" state
   */
  intermediate?: boolean;

  /**
   * Applies disabled styles to checkbox; Can be passed down from `FormItem`
   * or styled separately with `Form.CheckboxGroup`
   */
  disabled?: boolean;

  /**
   * Callback function called when value of checkbox changes
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * Singular checkbox input.
 */
const Checkbox = React.forwardRef<HTMLInputElement, ICheckboxProps>(
  (
    {
      children,
      disabled,
      onChange,
      name,
      defaultChecked,
      checked: controlledChecked,
      intermediate,
      id,
      value,
      ...props
    },
    ref
  ) => {
    const hiddenCheckboxRef = ref || React.createRef<HTMLInputElement>();
    const [uncontrolledChecked, setChecked] = React.useState(
      defaultChecked || false
    );
    const { groupDisabled, groupOnChange } = useCheckboxGroupContext();
    const isDisabled = disabled || groupDisabled;
    const checked = controlledChecked || uncontrolledChecked;

    /**
     * On change handler for input that uses change handler from props or
     * sets state internally if no change handler is passed in.
     *
     * @param event The native event passed to change handler
     */
    const changeHandler: React.ChangeEventHandler<HTMLInputElement> = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (typeof groupOnChange !== 'undefined') {
        groupOnChange(event);
      }
      if (typeof onChange !== 'undefined') {
        onChange(event);
      } else {
        setChecked(!checked);
      }
    };

    return (
      <>
        <StyledHiddenCheckbox
          ref={hiddenCheckboxRef}
          name={name}
          disabled={isDisabled}
          onChange={changeHandler}
          aria-checked={checked}
          checked={checked}
          id={id}
          value={value}
        />
        <StyledCheckbox
          data-intermediate={intermediate}
          checked={checked}
          disabled={isDisabled}
          borderRadius={1}
          onClick={() => {
            /**
             * When the visible styled checkbox is clicked, trigger a
             * click event on the hidden checkbox.
             */
            if (
              hiddenCheckboxRef &&
              typeof hiddenCheckboxRef === 'object' &&
              hiddenCheckboxRef.current
            ) {
              hiddenCheckboxRef.current.click();
            }
          }}
          {...props}
        >
          <StyledCheckboxIcon
            checked={checked}
            viewBox={intermediate ? '0 0 18 18' : '-2 -2 18 18'}
          >
            {children}
            {intermediate ? (
              <rect x="4" y="8" width="10" height="2" rx="1" />
            ) : (
              <path d="M11.9760773,1.87305822 C11.6816474,1.6616326 11.2717206,1.7294814 11.0604136,2.02407652 L4.92026113,10.5896595 L2.87062705,8.53712377 C2.61425873,8.28061152 2.19864456,8.28061152 1.94227624,8.53712377 C1.68590792,8.79363601 1.68590792,9.20948351 1.94227624,9.46599575 L1.94227624,9.46599575 L4.53877108,12.0635105 C4.80432665,12.3191473 5.22650315,12.3108304 5.4819965,12.0455634 C5.50124599,12.0254277 5.51918303,12.0044164 5.53580759,11.982092 L12.1270108,2.78967365 C12.3383178,2.49464079 12.2705071,2.08448384 11.9760773,1.87305822 Z" />
            )}
          </StyledCheckboxIcon>
        </StyledCheckbox>
      </>
    );
  }
);

Checkbox.displayName = 'Form.Checkbox';

export default Checkbox;
